<template>
  <div class="desk-event-editor" @keyup.esc="close">
    <div class="content">
      <div class="editor-header">
        <div>
          <div class="editor-title">{{ isNew ? 'New Booking' : 'Edit Booking' }}</div>
          <div class="editor-subtitle" v-if="deskTitle">{{ deskTitle }}</div>
        </div>
        <button type="button" class="editor-close" title="Close" @click.prevent="close">
          <i class="ion ion-md-close"></i>
        </button>
      </div>

      <div class="editor-body">
        <div class="editor-alert editor-alert_danger" v-if="errorsRaw.length">
          <i class="ion ion-md-close-circle editor-alert-icon"></i>
          <div>
            <div class="error" v-for="(error, i) in errorsRaw" :key="i">{{ error.message }}</div>
          </div>
        </div>

        <div class="field">
          <div class="field-label"><i class="ion ion-md-contact field-icon"></i>Employee</div>
          <div class="field-body">
            <b-input-group v-if="clientEvent.employee_id">
              <b-input :value="employeeName" :class="{ 'is-invalid': errorFields.employee_id }" disabled />
              <template #append>
                <b-btn title="Select another employee" @click="employeeClear"><i class="ion ion-md-close"></i></b-btn>
              </template>
            </b-input-group>
            <AppSelect
              v-else
              @search="employeeSearch"
              @select="employeeSelect"
              @nextPage="employeeNextPage"
              :items="employees"
              :propose-new="false"
              :is-valid="!errorFields.employee_id"
              placeholder="select employee"
            >
              <template #default="props">
                <span
                  :class="[
                    'select-item',
                    {
                      owner: userEmployee && userEmployee.email === props.item.email,
                      owner_bordered: userEmployee && userEmployee.email === props.item.email && employees.length > 1,
                      busy: employeeBusyMap[props.item.id]
                    }
                  ]"
                >
                  <AppAvatar class="mr-2" v-if="props.item.avatar_id" :src="getAvatar(props.item)" />
                  <span class="select-item-text">
                    <div class="employee-name">
                      {{ props.item.name }}
                      <span class="busy-badge" v-if="employeeBusyMap[props.item.id]">busy</span>
                    </div>
                    <div class="extra-title" v-if="getExtraTitle(props.item)" v-html="getExtraTitle(props.item)"></div>
                    <span class="email">{{ props.item.email }}</span>
                  </span>
                </span>
              </template>
            </AppSelect>
            <div class="editor-alert editor-alert_warning" v-if="employeeBusyWarning">
              <i class="ion ion-md-warning editor-alert-icon"></i>
              <div>{{ employeeBusyWarning }}</div>
            </div>
          </div>
        </div>

        <div class="field" v-if="!infinityChecked">
          <div class="field-label"><i class="ion ion-md-time field-icon"></i>When</div>
          <div class="field-body">
            <div class="datetime-row">
              <span class="datetime-label">From</span>
              <AppDateSelect
                class="datetime-date"
                :is-valid="!errorFields.start"
                :date="clientEvent.start"
                :marks-map="marksMap"
                :marks-format="marksFormat"
                :max-date="maxDate"
                @change="setStartDate"
              />
              <AppTimeSelect
                v-if="!allDayChecked"
                class="datetime-time"
                :is-valid="!errorFields.start"
                :time="startTime"
                @change="setStartTime"
              />
            </div>
            <div class="datetime-row">
              <span class="datetime-label">To</span>
              <AppDateSelect
                class="datetime-date"
                :is-valid="!errorFields.end"
                :date="clientEvent.end"
                :min-date="clientEvent.start"
                :marks-map="marksMap"
                :marks-format="marksFormat"
                :max-date="maxDate"
                @change="setEndDate"
              />
              <AppTimeSelect
                v-if="!allDayChecked"
                class="datetime-time"
                :is-valid="!errorFields.end"
                :from-time="endTimeLimit"
                :time="endTime"
                @change="setEndTime"
              />
            </div>
            <div class="field-note" v-if="timezoneMismatchNote" v-text="timezoneMismatchNote"></div>
          </div>
        </div>

        <div class="field">
          <div class="field-label"><i class="ion ion-md-options field-icon"></i>Options</div>
          <div class="field-body">
            <div class="option-row">
              <div class="form-check">
                <input
                  class="form-check-input"
                  type="checkbox"
                  id="infinityCheck"
                  :checked="infinityChecked"
                  :disabled="infinityDisabled || !isNew"
                  @change="infinityToggle"
                />
                <label class="form-check-label" for="infinityCheck">
                  <i class="ion ion-md-infinite mr-1"></i>Infinity Booking
                </label>
              </div>
              <div class="form-check" v-if="!infinityChecked">
                <input class="form-check-input" type="checkbox" id="allDayCheck" :checked="allDayChecked" @change="allDayToggle" />
                <label class="form-check-label" for="allDayCheck">
                  <i class="ion ion-md-sunny mr-1"></i>All Day
                </label>
              </div>
            </div>
            <div class="option-row" v-if="!infinityChecked && isLess24Hours">
              <div class="form-check">
                <input class="form-check-input" type="checkbox" id="repeatCheck" :checked="!!eventSet" @change="eventSetToggle" />
                <label class="form-check-label" for="repeatCheck">
                  <i class="ion ion-md-repeat mr-1"></i>Repeat at Same Time
                </label>
              </div>
            </div>
            <DeskEventSetEditor
              v-if="!infinityChecked && isLess24Hours && eventSet"
              :client-event="clientEvent"
              :event-set="eventSet"
              ref="eventSet"
              :limited="infinityDisabled"
            />
          </div>
        </div>

        <div class="field">
          <div class="field-label"><i class="ion ion-md-alarm field-icon"></i>Remind</div>
          <div class="field-body">
            <AppSimpleSelect :is-valid="!errorFields.remind" not-selected-title="Never" :options="remindOptions" @change="remindChange" />
          </div>
        </div>
      </div>

      <div class="editor-footer">
        <button class="btn btn-danger" v-if="!isNew" @click.prevent="destroy">Delete</button>
        <button class="btn btn-default ml-auto mr-2" @click.prevent="close">Cancel</button>
        <button class="btn btn-primary" :disabled="saving" @click.prevent="save">Save</button>
      </div>
    </div>
    <DeskEventSetDialog v-if="showEventSetSaveDialog" @submit="handleSaveDialogAnswer" @close="showEventSetSaveDialog = false" />
    <DeskEventSetDialog
      v-if="showEventSetDeleteDialog"
      @submit="handleDeleteDialogAnswer"
      @close="showEventSetDeleteDialog = false"
      is-delete
    />
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions, mapMutations } from 'vuex'
import AppSelect from '@/components/shared/AppSelect'
import AppDateSelect from '@/components/shared/AppDateSelect'
import AppTimeSelect from '@/components/shared/AppTimeSelect'
import { Api } from '@/utils/Api'
import UrlStore from '@/utils/UrlStore'
import AppSimpleSelect from '@/components/shared/AppSimpleSelect'
import { EMPLOYEE_LOAD } from '@/store/modules/employee'
import { isSameTimezone, SERVER_DATE_TIME_FORMAT, timezoneCity, toBuildingDate } from '@/components/desk/utils'
import { isLocalId } from '@/utils/Id'
import AppAvatar from '@/components/shared/AppAvatar'
import { getAvatarSrc } from '@/utils/Helpers'
import DeskEventSetEditor from '@/components/desk/DeskEventSetEditor'
import {
  convertClientEventToServerEvent,
  convertServerEventToClient,
  EventSaveTypes,
  getDeskHeader
} from '@/components/desk/helpers'
import { isEqual } from 'lodash'
import DeskEventSetDialog from '@/components/desk/DeskEventSetDialog'
import { MAP_UPDATE } from '@/store/modules/map'
import * as PN from '@/utils/PermissionNames'
import escape from 'lodash/escape'
import dayjs from 'dayjs'

const employeesApi = new Api(UrlStore.EMPLOYEES)
const deskEventSetsApi = new Api(UrlStore.DESK_EVENT_SETS)

export default {
  name: 'DeskEventEditor',
  components: {
    DeskEventSetDialog,
    DeskEventSetEditor,
    AppAvatar,
    AppSimpleSelect,
    AppTimeSelect,
    AppDateSelect,
    AppSelect
  },
  props: {
    event: {
      type: Object,
      required: true
    },
    marksMap: {
      type: Map,
      default: () => {
        return new Map()
      }
    },
    marksFormat: {
      type: String,
      default: 'YYYY-MM-DD'
    }
  },
  data: () => ({
    clientEvent: {},
    // true once the user has picked a date/time themselves — guards against the buildingTimezone
    // watcher clobbering an in-progress edit once floor/building data resolves
    userEditedTime: false,
    employees: [],
    allDayChecked: false,
    infinityChecked: false,
    userEmployee: null,
    errorFields: {},
    errorsRaw: [],
    saving: false,
    extraTitlesMap: new Map(),
    nextPageLoading: false,
    nextPagePromise: Promise.resolve(true),
    nextPage: 1,
    // employee_id -> the booking that overlaps the selected period (employees stay selectable,
    // an overlap only raises a warning — the server has the final say)
    employeeBusyMap: {},
    employeeBusyMapTimeoutId: null,
    showEventSetSaveDialog: false,
    showEventSetDeleteDialog: false,
    eventSet: null,
    repeatEnabled: false
  }),
  computed: {
    ...mapState('auth', ['user']),
    ...mapState('settings', ['generalSettings']),
    ...mapGetters('rbac', ['can']),
    ...mapGetters('employee', ['getEmployee']),
    ...mapGetters('department', ['getDepartment']),
    ...mapGetters('point', ['getPoint']),
    ...mapGetters('desk', ['getDesk', 'getDeskTimezone']),
    ...mapGetters('position', ['getPosition']),
    buildingTimezone() {
      return this.getDeskTimezone(this.event.desk_id) || dayjs.tz.guess()
    },
    timezoneMismatchNote() {
      const browserTimezone = dayjs.tz.guess()
      if (isSameTimezone(this.buildingTimezone, browserTimezone)) return ''
      return `Times are in ${timezoneCity(this.buildingTimezone)} time - your local zone is different.`
    },
    maxDate() {
      if (this.infinityDisabled) {
        const limit = this.generalSettings.general_max_days_desk_booking_limit
        return limit ? dayjs().tz(this.buildingTimezone).add(limit, 'day') : undefined
      }
      return undefined
    },
    infinityDisabled() {
      return !this.can(PN.DESK_EVENT_CREATE_INFINITY)
    },
    isNew() {
      return this.event.id && isLocalId(this.event.id)
    },
    hasChanges() {
      return !isEqual(this.event, convertClientEventToServerEvent(this.clientEvent, this.infinityChecked))
    },
    remindOptions() {
      const activeValue = this.clientEvent.remind
      return [
        { value: null, title: 'Never', active: activeValue === null },
        { value: 0, title: 'At time of event', active: activeValue === 0 },
        { value: -5, title: '5 minutes before', active: activeValue === -5 },
        { value: -15, title: '15 minutes before', active: activeValue === -15 },
        { value: -30, title: '30 minutes before', active: activeValue === -30 },
        { value: -60, title: '1 hour before', active: activeValue === -60 },
        { value: -120, title: '2 hours before', active: activeValue === -120 },
        { value: -720, title: '12 hours before', active: activeValue === -720 },
        { value: -1440, title: '1 day before', active: activeValue === -1440 },
        { value: -10080, title: '1 week before', active: activeValue === -10080 }
      ]
    },
    startDate() {
      return this.clientEvent.key_ && this.clientEvent.start.format('L US')
    },
    startTime() {
      return this.clientEvent.key_ && this.clientEvent.start.format('HH:mm')
    },
    endDate() {
      return this.clientEvent.key_ && this.clientEvent.end.format('L US')
    },
    endTime() {
      return this.clientEvent.key_ && this.clientEvent.end.format('HH:mm')
    },
    employeeName() {
      if (this.clientEvent.employee_id) {
        const employee = this.getEmployee(this.clientEvent.employee_id)
        if (employee) return employee.name
      }

      return ''
    },
    isLess24Hours() {
      return this.clientEvent.end && this.clientEvent.end.diff(this.clientEvent.start, 'hours') < 24
    },
    needDeleteEventSetOnUpdate() {
      return this.clientEvent.set_id && this.eventSet === null
    },
    canBookForOthers() {
      return this.can(PN.DESK_EVENT_BOOK_FOR_OTHERS)
    },
    endTimeLimit() {
      if (this.clientEvent.start.format('YYYY-MM-DD') === this.clientEvent.end.format('YYYY-MM-DD')) {
        return this.startTime
      }
      return ''
    },
    startTimeLimit() {
      if (this.clientEvent.start.format('YYYY-MM-DD') === this.clientEvent.end.format('YYYY-MM-DD')) {
        return this.endTime
      }
      return ''
    },
    isTodayStartEvent() {
      return this.clientEvent.start.format('YYYY-MM-DD') === dayjs().tz(this.buildingTimezone).format('YYYY-MM-DD')
    },
    deskTitle() {
      const desk = this.getDesk(this.event.desk_id)
      return desk ? getDeskHeader(desk) : ''
    },
    selectedEmployeeBusyEvent() {
      return (this.clientEvent.employee_id && this.employeeBusyMap[this.clientEvent.employee_id]) || null
    },
    employeeBusyWarning() {
      const busyEvent = this.selectedEmployeeBusyEvent
      if (!busyEvent) return ''

      const desk = this.getDesk(busyEvent.desk_id)
      const where = desk ? ` at ${getDeskHeader(desk)}` : ''
      const name = this.employeeName || 'This employee'

      return `${name} already has a booking${where} ${this.formatBusyPeriod(busyEvent)}.`
    }
  },
  methods: {
    ...mapActions('point', ['pointSave', 'pointLoad']),
    ...mapActions('desk', ['deskEventSave', 'deskEventDestroy', 'deskEventsSearch']),
    ...mapMutations('map', {
      mapUpdate_: MAP_UPDATE
    }),
    ...mapMutations('employee', {
      employeeLoad: EMPLOYEE_LOAD
    }),
    employeeClear() {
      this.clientEvent.employee_id = null
      this.employees = []
    },
    getAvatar(employee) {
      return getAvatarSrc(employee.avatar_id, employee.avatar_updated_at)
    },
    getExtraTitle(user) {
      if (!this.extraTitlesMap.has(user.id)) {
        let title = ''
        if (user.department_id) {
          const department = this.getDepartment(user.department_id)
          if (department) {
            title += `<div class="department">${escape(department.name)}</div>`
          }
        }

        if (user.position_id) {
          const position = this.getPosition(user.position_id)
          if (position) {
            title += `<div class="position">${escape(position.name)}</div>`
          }
        }

        this.extraTitlesMap.set(user.id, title)
      }

      return this.extraTitlesMap.get(user.id)
    },
    formatBusyPeriod(busyEvent) {
      const start = dayjs(busyEvent.start, SERVER_DATE_TIME_FORMAT).tz(this.buildingTimezone)
      if (busyEvent.end === 'infinity') return `from ${start.format('MMM D, HH:mm')} with no end date`

      const end = dayjs(busyEvent.end, SERVER_DATE_TIME_FORMAT).tz(this.buildingTimezone)
      const endFormat = start.isSame(end, 'day') ? 'HH:mm' : 'MMM D, HH:mm'
      return `on ${start.format('MMM D, HH:mm')} - ${end.format(endFormat)}`
    },
    loadEmployeeBusyMap() {
      if (this.employeeBusyMapTimeoutId) {
        clearTimeout(this.employeeBusyMapTimeoutId)
      }
      this.employeeBusyMapTimeoutId = setTimeout(async () => {
        // an infinity booking has no interval to compare against — the server validates it
        if (this.infinityChecked || !this.clientEvent.start || !this.clientEvent.end) {
          this.employeeBusyMap = {}
          return
        }

        const response = await this.deskEventsSearch({
          params: {
            filters: {
              since: this.clientEvent.start.format(SERVER_DATE_TIME_FORMAT),
              until: this.clientEvent.end.format(SERVER_DATE_TIME_FORMAT)
            },
            pagerDisabled: true
          }
        })
        if (!response || response.status !== 200) return

        const busyMap = {}
        response.data.items.forEach((deskEvent) => {
          // the booking being edited never conflicts with itself
          if (deskEvent.id === this.event.id) return

          // rebooking allowed: a temporary booking may overlap the employee's infinity one,
          // but not another temporary booking of theirs
          if (deskEvent.end === 'infinity' && !this.user.rebooking_infinity_locked) return

          if (!busyMap[deskEvent.employee_id]) {
            busyMap[deskEvent.employee_id] = deskEvent
          }
        })

        this.employeeBusyMap = busyMap
      }, 600)
    },

    async save() {
      if (this.hasChanges || this.$refs.eventSet?.hasChanges) {
        if (this.eventSet) {
          if (this.isNew) {
            await this.saveEventSet(EventSaveTypes.All)
          } else {
            this.showEventSetSaveDialog = true
          }
        } else {
          await this.saveEvent()
        }
      }
    },
    async handleSaveDialogAnswer(type) {
      this.showEventSetSaveDialog = false
      switch (type) {
        case EventSaveTypes.CurrentOnly:
          this.clientEvent.set_id = null
          await this.saveEvent()
          break
        default:
          await this.saveEventSet(type)
      }
    },
    async saveEventSet(type) {
      if (this.saving) return
      try {
        this.saving = true
        await this.$refs.eventSet.save(type)
        this.close()
      } catch (error) {
        this.handleSaveError(error)
      } finally {
        this.saving = false
      }
    },
    async saveEvent() {
      if (this.saving) return
      try {
        this.saving = true
        if (this.allDayChecked && this.isTodayStartEvent) {
          this.clientEvent.start = dayjs().tz(this.buildingTimezone).add(10, 'seconds')
        }
        if (this.needDeleteEventSetOnUpdate) {
          deskEventSetsApi.destroy({ id: this.clientEvent.set_id })
          this.clientEvent.set_id = null
        }
        const deskEvent = convertClientEventToServerEvent(this.clientEvent, this.infinityChecked)
        await this.deskEventSave({ deskEvent })
        this.errorFields = {}
        this.errorsRaw = []
        if (deskEvent.end === 'infinity') {
          await this.updatePoint()
        }
        this.close()
      } catch (error) {
        this.handleSaveError(error)
      } finally {
        this.saving = false
      }
    },
    handleSaveError(error) {
      this.saving = false
      if (error && error.response && error.response.status === 422) {
        error.response.data.forEach((error) => {
          this.$set(this.errorFields, error.field, true)
        })
        this.errorsRaw = error.response.data
      } else {
        throw error
      }
    },

    async destroy() {
      if (this.eventSet) {
        if (this.isNew) {
          await this.destroyEvent()
        } else {
          this.showEventSetDeleteDialog = true
        }
      } else {
        await this.destroyEvent()
      }
    },
    async handleDeleteDialogAnswer(type) {
      this.showEventSetDeleteDialog = false
      switch (type) {
        case EventSaveTypes.CurrentOnly:
          await this.destroyEvent()
          break
        default:
          await this.destroyEventSet(type)
      }
    },
    destroyEvent() {
      this.deskEventDestroy(this.event)
        .then(() => {
          this.$appToast.success('Booking deleted')
          this.close()
        })
        .catch((error) => {
          if (error.response) {
            if (error.response.status === 404) {
              this.$appToast.error('Booking not found!')
            } else if (error.response.status === 500) {
              this.$appToast.error('Internal server on booking delete!')
            }
          } else {
            throw error
          }
        })
    },
    async destroyEventSet(type) {
      await this.$refs.eventSet.destroy(type)
      this.close()
    },

    close() {
      this.$emit('close')
    },
    allDayToggle() {
      this.userEditedTime = true
      if (this.allDayChecked) {
        this.allDayChecked = false
      } else {
        this.allDayChecked = true
        this.clientEvent.start = this.clientEvent.start.set('hour', 0).set('minute', 0).set('second', 0)
        this.clientEvent.end = this.clientEvent.end.set('day', this.clientEvent.start.get('day')).set('hour', 23).set('minute', 59).set('second', 59)
      }
      this.clientEvent.key_++
      this.loadEmployeeBusyMap()
    },
    infinityToggle() {
      this.infinityChecked = !this.infinityChecked
      this.loadEmployeeBusyMap()
    },
    employeeNextPage(term) {
      // If user cannot book for others, no pagination needed
      if (!this.canBookForOthers) {
        return
      }

      if (!this.nextPageLoading) {
        this.nextPageLoading = true
        const config = { params: { term, sort: 'name', page: ++this.nextPage } }
        this.nextPagePromise = employeesApi.getAll(config).then((r) => {
          if (r.data.pager.currentPage <= r.data.pager.pageCount) {
            this.nextPageLoading = false
          }
          if (this.userEmployee) {
            this.employees = [this.userEmployee, ...this.employees, ...r.data.items]
          } else {
            this.employees = [...this.employees, ...r.data.items]
          }
        })
      }
    },
    employeeSearch(term) {
      this.nextPage = 1

      // If user cannot book for others, only show current user or nothing
      if (!this.canBookForOthers) {
        if (this.userEmployee) {
          this.employees = [this.userEmployee]
        } else {
          this.employees = []
        }
        return
      }

      const config = {
        params: {
          term,
          sort: 'name'
        }
      }

      employeesApi.getAll(config).then((r) => {
        if (this.userEmployee) {
          this.employees = [this.userEmployee, ...r.data.items]
        } else {
          this.employees = r.data.items
        }
      })
    },
    employeeSelect(employee) {
      this.employeeLoad(employee)
      this.clientEvent.employee_id = employee.id
    },
    userEmployeeLoad() {
      const config = { params: { email: this.user.email } }
      employeesApi.getAll(config).then((r) => {
        this.userEmployee = r.data.items[0]
        this.prefillUserEmployee()
      })
    },
    prefillUserEmployee() {
      if (this.isNew && !this.canBookForOthers && this.userEmployee && !this.clientEvent.employee_id) {
        this.employeeSelect(this.userEmployee)
      }
    },
    async loadCurrentEventSet() {
      const response = await deskEventSetsApi.get({ id: this.event.set_id })
      return response.data
    },
    async initEventSet() {
      this.eventSet = this.event.set_id ? await this.loadCurrentEventSet() : null
    },
    async eventSetToggle() {
      if (this.eventSet) {
        this.eventSet = null
      } else {
        this.eventSet = this.event.set_id ? await this.loadCurrentEventSet() : {}
      }
    },
    remindChange(selectedOption) {
      this.clientEvent.remind = selectedOption.value
    },
    setStartDate(date) {
      this.userEditedTime = true
      // Calculate duration in minutes (excluding seconds) before changing start
      const startNormalized = this.clientEvent.start.startOf('minute')
      const endNormalized = this.clientEvent.end.startOf('minute')
      const durationMinutes = endNormalized.diff(startNormalized, 'minute')

      // Set new start date with time preserved
      this.clientEvent.start = toBuildingDate(date, this.buildingTimezone)
        .set('hour', this.clientEvent.start.get('hour'))
        .set('minute', this.clientEvent.start.get('minute'))
        .set('second', 0)

      // Calculate new end preserving duration in minutes, clamped to booking limit
      let newEnd = this.clientEvent.start.add(durationMinutes, 'minute').set('second', 59)
      if (this.maxDate && newEnd.isAfter(this.maxDate, 'day')) {
        newEnd = this.maxDate.set('hour', newEnd.get('hour')).set('minute', newEnd.get('minute')).set('second', 59)
      }
      this.clientEvent.end = newEnd
      this.clientEvent.key_++

      this.loadEmployeeBusyMap()
    },
    setEndDate(date) {
      this.userEditedTime = true
      this.clientEvent.end = toBuildingDate(date, this.buildingTimezone)
        .set('hour', this.clientEvent.end.get('hour'))
        .set('minute', this.clientEvent.end.get('minute'))
        .set('second', 59)
      this.clientEvent.key_++
      this.loadEmployeeBusyMap()
    },
    setStartTime(time) {
      this.userEditedTime = true
      // Calculate duration in minutes (excluding seconds) before changing start
      const startNormalized = this.clientEvent.start.startOf('minute')
      const endNormalized = this.clientEvent.end.startOf('minute')
      const durationMinutes = endNormalized.diff(startNormalized, 'minute')

      // Set new start time
      this.clientEvent.start = this.clientEvent.start
        .set('hour', time.hour)
        .set('minute', time.minute)
        .set('second', 0)

      // Calculate new end preserving duration in minutes
      this.clientEvent.end = this.clientEvent.start
        .add(durationMinutes, 'minute')
        .set('second', 59)

      this.clientEvent.key_++
      this.loadEmployeeBusyMap()
    },
    setEndTime(time) {
      this.userEditedTime = true
      this.clientEvent.end = this.clientEvent.end
        .set('hour', time.hour)
        .set('minute', time.minute)
        .set('second', 59)
      this.clientEvent.key_++
      this.loadEmployeeBusyMap()
    },
    async updatePoint() {
      const desk = this.getDesk(this.event.desk_id)
      if (desk) {
        await this.pointLoad({ id: desk.point_id, params: { expand: 'employee' } })
      }
    }
  },
  created() {
    this.userEmployeeLoad()
  },
  beforeDestroy() {
    if (this.employeeBusyMapTimeoutId) {
      clearTimeout(this.employeeBusyMapTimeoutId)
    }
  },
  watch: {
    event: {
      immediate: true,
      handler(event) {
        this.userEditedTime = false
        this.clientEvent = convertServerEventToClient(event, this.buildingTimezone)
        this.loadEmployeeBusyMap()
      }
    },
    // floor/building data can still be loading when this editor first mounts (the router doesn't
    // gate on it), so buildingTimezone can start out as a browser-guess fallback and only resolve
    // to the real building zone a moment later. Re-derive clientEvent from the original event once
    // that happens — but only if the user hasn't already started picking their own date/time.
    buildingTimezone(newTimezone, oldTimezone) {
      if (oldTimezone && !this.userEditedTime) {
        this.clientEvent = convertServerEventToClient(this.event, newTimezone)
        this.loadEmployeeBusyMap()
      }
    },
    clientEvent() {
      this.prefillUserEmployee()
    },
    'event.set_id': {
      immediate: true,
      handler() {
        this.initEventSet()
      }
    }
  }
}
</script>


<style scoped lang="scss">
.desk-event-editor {
  position: absolute;
  right: 0;
  left: 0;
  top: 0;
  width: 100%;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: flex-start;
  height: calc(100vh - 66px);
  z-index: 1;
  overflow-y: auto;
  padding: 20px 0;
}

.content {
  background: #fff;
  border-radius: 6px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.25);
  margin: auto 20px;
  width: 100%;
  max-width: 480px;

  @media (max-width: 380px) {
    margin: auto 0;
    border-radius: 0;
  }
}

.editor-header {
  display: flex;
  align-items: flex-start;
  padding: 16px 20px;
  border-bottom: 1px solid #eee;
}

.editor-title {
  font-size: 16px;
  font-weight: 600;
  line-height: 1.2;
}

.editor-subtitle {
  margin-top: 2px;
  font-size: 12px;
  color: #999;
}

.editor-close {
  margin-left: auto;
  padding: 0 0 0 10px;
  border: 0;
  background: none;
  font-size: 20px;
  line-height: 1;
  color: #999;
  cursor: pointer;

  &:hover {
    color: #333;
  }
}

.editor-body {
  padding: 16px 20px;
}

.editor-footer {
  display: flex;
  align-items: center;
  padding: 14px 20px;
  border-top: 1px solid #eee;
}

// the editor lives inside the map point panel (~350px wide on a desktop), so fields stack:
// label on top, control below, both full width
.field {
  margin-bottom: 16px;

  &:last-child {
    margin-bottom: 0;
  }
}

.field-label {
  display: flex;
  align-items: center;
  margin-bottom: 5px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #888;
}

.field-icon {
  width: 16px;
  margin-right: 6px;
  font-size: 15px;
  text-align: center;
}

.field-body {
  min-width: 0;

  // AppSelect renders a bootstrap .row/.col wrapper — drop its gutters inside the field grid
  ::v-deep .row {
    margin: 0;
  }

  ::v-deep .col-sm-12 {
    padding: 0;
  }
}

.field-note {
  margin-top: 4px;
  font-size: 12px;
  color: #999;
}

.datetime-row {
  display: flex;
  align-items: center;
  margin-bottom: 6px;

  &:last-of-type {
    margin-bottom: 0;
  }
}

.datetime-label {
  flex: 0 0 34px;
  font-size: 12px;
  color: #999;
}

.datetime-date {
  flex: 1 1 60%;
  margin-right: 6px;
}

.datetime-time {
  flex: 1 1 40%;
}

.option-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 24px;
  padding-top: 6px;
}

.editor-alert {
  display: flex;
  align-items: flex-start;
  margin-bottom: 14px;
  padding: 8px 10px;
  border-radius: 4px;
  font-size: 13px;
  line-height: 1.35;

  &_danger {
    background: #fdecea;
    color: #a12622;
  }

  &_warning {
    margin: 6px 0 0;
    background: #fff6e0;
    color: #8a6100;
  }
}

.editor-alert-icon {
  margin-right: 8px;
  font-size: 16px;
  line-height: 1.2;
}

.select-item {
  display: flex;
  align-items: center;

  &.busy {
    opacity: 0.75;
  }
}

.busy-badge {
  margin-left: 6px;
  padding: 0 5px;
  border-radius: 8px;
  background: #fff6e0;
  color: #8a6100;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  vertical-align: middle;
}

.email {
  display: block;
  font-size: 90%;
}

.owner_bordered {
  border-bottom: 1px solid #ccc;
}

.employee-name {
  font-weight: bold;
}

.extra-title {
  white-space: nowrap;
  overflow-x: hidden;
  text-overflow: clip;

  & > div {
    max-width: 100%;
  }
}
</style>
